import { useEffect, useRef, useState } from 'react';
import { useLanguage } from '../../context/LanguageContext';
import { motion } from 'framer-motion';
import { GLASS } from './glass';

const COPY = {
    en: {
        eyebrow: 'What the category gets wrong',
        heading: 'A full label doesn’t make a good formula.',
        body: 'Listing ingredients and doses is the bare minimum. The real questions come after that: why this ingredient, why this amount, and does it belong in something you take every evening?',
        col_category: 'The category norm',
        col_sleepstack: 'The Sleepstack standard',
        rows: [
            {
                key: 'dosages',
                topic: 'Doses',
                category: 'On the label, but not always at the amounts used in research.',
                sleepstack: 'Dosed in line with published research. Meaningful amounts, not token ones.',
                sleepstack_mobile: 'In line with published research. Meaningful, not token.',
            },
            {
                key: 'melatonin',
                topic: 'Melatonin',
                category: 'Practically standard in sleep products.',
                sleepstack: 'Melatonin is a hormone. We leave it out on purpose.',
            },
            {
                key: 'sucralose',
                topic: 'Sucralose',
                category: 'The go-to for sweetening powders.',
                sleepstack: 'Research on its metabolic and gut effects is mixed. So we take a different route.',
                sleepstack_mobile: 'The research is mixed. So we take a different route.',
            },
            {
                key: 'supporting',
                topic: 'Supporting ingredients',
                category: 'Added for flavour, colour, texture and production.',
                sleepstack: 'Only when they earn their place. From natural sources where possible.',
            },
            {
                key: 'quality',
                topic: 'Raw materials',
                category: 'A trade-off between quality, process and cost.',
                sleepstack: 'The better raw material wins, even when it costs more.',
            },
            {
                key: 'blend',
                topic: 'Blends',
                category: 'Complexes and blends are everywhere.',
                sleepstack: 'No proprietary blends. Nothing hidden.',
            },
        ],
    },
    nl: {
        eyebrow: 'Het probleem in de markt',
        heading: 'Een volledig etiket is nog geen goede formule.',
        body: 'Ingrediënten en doseringen vermelden is de basis. Voor ons begint de echte vraag daarna: waarom deze stof, waarom deze hoeveelheid en past die keuze bij een dagelijkse avondroutine?',
        col_category: 'Gangbaar in de markt',
        col_sleepstack: 'De Sleepstack-standaard',
        rows: [
            {
                key: 'dosages',
                topic: 'Doseringen',
                category: 'Wel op het etiket. Niet altijd op onderzoeksniveau.',
                sleepstack: 'Doseringen gebaseerd op gepubliceerd onderzoek. Logisch gedoseerd, niet symbolisch.',
                sleepstack_mobile: 'Gebaseerd op gepubliceerd onderzoek. Logisch gedoseerd, niet symbolisch.',
            },
            {
                key: 'melatonin',
                topic: 'Melatonine',
                category: 'Bijna standaard in de slaapcategorie.',
                sleepstack: 'Melatonine is een hormoon. Dat voegen wij bewust niet toe.',
            },
            {
                key: 'sucralose',
                topic: 'Sucralose',
                category: 'Veelgebruikt om poeders zoeter te maken.',
                sleepstack: 'Onderzoek naar metabole en darm-effecten is niet eenduidig. Wij kiezen liever anders.',
                sleepstack_mobile: 'Onderzoek is niet eenduidig. Wij kiezen liever anders.',
            },
            {
                key: 'supporting',
                topic: 'Hulpstoffen',
                category: 'Voor smaak, kleur, textuur en productie.',
                sleepstack: 'Alleen als ze echt iets toevoegen. Liefst van natuurlijke oorsprong.',
            },
            {
                key: 'quality',
                topic: 'Grondstofkeuze',
                category: 'Kwaliteit, techniek en kostprijs worden afgewogen.',
                sleepstack: 'De betere grondstof wint. Ook als die meer kost.',
            },
            {
                key: 'blend',
                topic: 'Blends',
                category: 'Complexen en blends zijn gebruikelijk.',
                sleepstack: 'Geen proprietary blend. Alles blijft zichtbaar.',
            },
        ],
    },
};

export function ProblemAnswerSection() {
    const { lang } = useLanguage();
    const c = COPY[lang] || COPY.en;

    // Active-row scroll tracking
    const rowRefs = useRef([]);
    const [activeIndex, setActiveIndex] = useState(0);

    useEffect(() => {
        rowRefs.current = rowRefs.current.slice(0, c.rows.length);
        const compute = () => {
            const focalY = window.innerHeight * 0.45; // ~mid-upper viewport
            let bestIdx = 0;
            let bestDist = Infinity;
            rowRefs.current.forEach((el, i) => {
                if (!el) return;
                const rect = el.getBoundingClientRect();
                const center = rect.top + rect.height / 2;
                const dist = Math.abs(center - focalY);
                if (dist < bestDist) {
                    bestDist = dist;
                    bestIdx = i;
                }
            });
            setActiveIndex((prev) => (prev === bestIdx ? prev : bestIdx));
        };
        compute();
        window.addEventListener('resize', compute);
        return () => {
            window.removeEventListener('resize', compute);
        };
    }, [c.rows.length]);

    return (
        <section className="section-padding relative" id="why" style={{ scrollMarginTop: '120px' }}>
            <div className="section-container">
                {/* Section intro */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5 }}
                    className={`relative mt-10 max-w-2xl mb-6 md:mb-8 p-6 sm:p-8 lg:p-10 ${GLASS}`}
                    data-testid="problem-block"
                >
                    <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] font-bold text-muted-foreground absolute -top-8 left-1">
                        <span className="w-8 h-px bg-muted-foreground" />
                        {c.eyebrow}
                    </div>
                    <h2 className="heading-lg text-foreground mb-4">
                        {lang === 'nl' ? (
                            <>
                                <span className="block">Een <span className="text-primary">volledig etiket</span></span>
                                <span className="block">is nog geen</span>
                                <span className="block text-secondary-dark">goede formule.</span>
                            </>
                        ) : (
                            <>
                                <span className="block">A <span className="text-primary">full label</span></span>
                                <span className="block">doesn’t make</span>
                                <span className="block text-secondary-dark">a good formula.</span>
                            </>
                        )}
                    </h2>
                    <p className="text-base md:text-lg text-slate-700 leading-relaxed">{c.body}</p>
                </motion.div>

                {/* Comparison */}
                <div className={`max-w-4xl p-5 sm:p-8 lg:p-10 ${GLASS}`} data-testid="standard-comparison">
                    {/* Mobile column headers: 2 columns, aligned with the category/sleepstack pair below */}
                    <div className="md:hidden grid grid-cols-2 gap-x-4 pb-2 mb-1 border-b border-slate-200">
                        <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-400 leading-tight">
                            {c.col_category}
                        </div>
                        <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-primary leading-tight">
                            Sleepstack
                        </div>
                    </div>

                    {/* Desktop column headers: 3 columns aligned with the desktop row grid */}
                    <div className="hidden md:grid md:grid-cols-[minmax(150px,0.85fr)_2fr_3.4fr] gap-x-8 pb-3 border-b border-slate-200">
                        <div />
                        <div className="text-[10px] uppercase tracking-[0.22em] font-semibold text-slate-400">
                            {c.col_category}
                        </div>
                        <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-primary">
                            {c.col_sleepstack}
                        </div>
                    </div>

                    {c.rows.map((row, i) => {
                        const isActive = false;
                        return (
                            <div
                                key={row.key}
                                ref={(el) => (rowRefs.current[i] = el)}
                                data-testid={`comparison-row-${row.key}`}
                                data-active={isActive ? 'true' : 'false'}
                                className={`relative grid grid-cols-2 md:grid-cols-[minmax(150px,0.85fr)_2fr_3.4fr] gap-x-4 md:gap-x-8 gap-y-1.5 md:gap-y-0 items-baseline transition-colors duration-[240ms] ease-out
                                    [grid-template-areas:'topic_topic''category_sleepstack']
                                    md:[grid-template-areas:'topic_category_sleepstack']
                                    ${i < c.rows.length - 1 ? 'border-b border-slate-200/70' : ''}
                                    ${isActive ? 'bg-primary/[0.045]' : 'bg-transparent'}
                                    py-3 md:py-[18px] -mx-3 md:-mx-4 px-3 md:px-4 rounded-[6px]`}
                            >
                                {/* Pink accent bar on active */}
                                <span
                                    aria-hidden="true"
                                    className={`absolute left-0 top-3 bottom-3 w-[3px] rounded-full bg-secondary transition-opacity duration-[240ms] ease-out ${
                                        isActive ? 'opacity-100' : 'opacity-0'
                                    }`}
                                />

                                {/* Topic */}
                                <div
                                    style={{ gridArea: 'topic' }}
                                    className={`font-condensed uppercase tracking-tight leading-none text-[19px] md:text-[18px] mb-0.5 md:mb-0 transition-colors duration-[240ms] ease-out ${
                                        isActive ? 'text-primary font-bold' : 'text-foreground font-bold'
                                    }`}
                                >
                                    {row.topic}
                                </div>

                                {/* Category */}
                                <p
                                    style={{ gridArea: 'category' }}
                                    className="text-[12.5px] md:text-sm font-normal text-slate-500 leading-snug"
                                >
                                    {row.category}
                                </p>

                                {/* Sleepstack */}
                                <p
                                    style={{ gridArea: 'sleepstack' }}
                                    className={`text-[13px] md:text-[15px] leading-snug font-medium md:font-medium transition-colors duration-[240ms] ease-out ${
                                        isActive ? 'text-foreground font-semibold' : 'text-slate-700'
                                    }`}
                                >
                                    <span className="md:hidden">{row.sleepstack_mobile || row.sleepstack}</span>
                                    <span className="hidden md:inline">{row.sleepstack}</span>
                                </p>
                            </div>
                        );
                    })}
                </div>
            </div>
        </section>
    );
}
